import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Button, FormError, Loading, TextField } from "../../components/ui";
import { AuthLayout } from "../../layouts/AuthLayout";
import { Link, navigate } from "../../router/router";
import { t } from "../../i18n";
import { apiOrigin, getServerUrl, isApp, normalizeServerUrl, setServerUrl } from "../../lib/platform";

function Heading({ title, intro }: { title: string; intro?: string }) {
  return (
    <>
      <h1>{title}</h1>
      <div className="lane" aria-hidden />
      {intro && <p className="intro">{intro}</p>}
    </>
  );
}

/** Lê o token do fragmento (#token=...) — não vai para o servidor nem para logs de acesso. */
function tokenFromHash(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
}

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const fields = error?.fieldErrors() ?? {};
  return (
    <AuthLayout>
      <Heading title={t("Entrar")} intro={t("Acesse sua assessoria com o e-mail cadastrado.")} />
      <form className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField
          label={t("E-mail")}
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fields.email}
        />
        <TextField
          label={t("Senha")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fields.password}
        />
        <Button type="submit" variant="primary" busy={busy} block>
          {t("Entrar")}
        </Button>
      </form>
      <div className="auth-links">
        <Link to="/esqueci-senha">{t("Esqueci minha senha")}</Link>
        {isApp && (
          <Link to="/servidor">{t("Servidor: {host} (alterar)", { host: getServerUrl()?.replace(/^https?:\/\//, "") ?? "—" })}</Link>
        )}
      </div>
    </AuthLayout>
  );
}

/** App de celular: escolhe o servidor EAPace (na rede local, o endereço do computador que roda o EAPace). */
export function ServerPage() {
  const [value, setValue] = useState(getServerUrl() ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const url = normalizeServerUrl(value);
    if (!url) return setError(t("Informe o endereço do servidor, por exemplo 192.168.0.10."));
    setBusy(true);
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 8000);
      const res = await fetch(`${url}/api/health/live`, { signal: ctl.signal, credentials: "omit" });
      clearTimeout(timer);
      if (!res.ok) throw new Error("http");
      setServerUrl(url);
      window.location.replace("/entrar");
    } catch {
      setError(
        t("Não foi possível falar com {url}. Confira o endereço, se o celular está na mesma rede Wi-Fi e se o certificado do EAPace foi instalado no app.", { url }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Heading title={t("Servidor")} intro={t("Informe o endereço do EAPace na sua rede (o computador onde o sistema está instalado).")} />
      <form className="form" onSubmit={submit} noValidate>
        {error && <p className="alert alert-danger" role="alert">{error}</p>}
        <TextField
          label={t("Endereço do servidor")}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="192.168.0.10"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="submit" variant="primary" busy={busy} block>
          {t("Testar e salvar")}
        </Button>
      </form>
      {apiOrigin() && (
        <div className="auth-links">
          <Link to="/entrar">{t("Voltar para o login")}</Link>
        </div>
      )}
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ message: string }>("/auth/forgot-password", { email });
      setDone(res.message);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Heading title={t("Redefinir senha")} intro={t("Enviaremos um link para você criar uma nova senha.")} />
      {done ? (
        <div className="alert alert-ok" role="status">
          <p>{done}</p>
        </div>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          {error && error.code !== "validation_error" && <FormError error={error} />}
          <TextField
            label={t("E-mail")}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error?.fieldErrors().email}
          />
          <Button type="submit" variant="primary" busy={busy} block>
            {t("Enviar link")}
          </Button>
        </form>
      )}
      <div className="auth-links">
        <Link to="/entrar">{t("Voltar para o login")}</Link>
      </div>
    </AuthLayout>
  );
}

/** Usado tanto para aceitar convite quanto para redefinir senha (o backend sabe o propósito). */
export function SetPasswordPage({ mode }: { mode: "invite" | "reset" }) {
  const [token] = useState(tokenFromHash);
  const [info, setInfo] = useState<{ email: string; full_name: string } | null>(null);
  const [checkError, setCheckError] = useState<ApiError | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setCheckError(new ApiError(400, "invalid_token", t("Link incompleto. Abra o link exatamente como recebido no e-mail.")));
      return;
    }
    api
      .post<{ email: string; full_name: string }>("/auth/token-info", { token })
      .then(setInfo)
      .catch(setCheckError);
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/set-password", { token, password });
      setDone(true);
      window.history.replaceState(null, "", window.location.pathname); // remove o token da barra
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const title = mode === "invite" ? t("Criar sua senha") : t("Nova senha");
  if (done) {
    return (
      <AuthLayout>
        <Heading title={t("Senha definida")} />
        <div className="alert alert-ok" role="status">
          <p>{t("Tudo certo. Entre com seu e-mail e a nova senha.")}</p>
        </div>
        <div style={{ marginTop: 16 }}>
          <Link to="/entrar" className="btn btn-primary btn-block">
            {t("Ir para o login")}
          </Link>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <Heading
        title={title}
        intro={info ? `${info.full_name} (${info.email})` : undefined}
      />
      {checkError ? (
        <>
          <FormError error={checkError} />
          <div className="auth-links">
            <Link to="/esqueci-senha">{t("Pedir um novo link")}</Link>
          </div>
        </>
      ) : !info ? (
        <Loading label={t("Validando o link…")} />
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          {error && <FormError error={error} />}
          <TextField
            label={t("Senha")}
            type="password"
            autoComplete="new-password"
            hint={t("Mínimo de 10 caracteres, com letras e números.")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <TextField
            label={t("Confirme a senha")}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch ? t("As senhas não conferem.") : undefined}
            required
          />
          <Button type="submit" variant="primary" busy={busy} block>
            {t("Salvar senha")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [state, setState] = useState<"loading" | "ok" | ApiError>("loading");
  useEffect(() => {
    const token = tokenFromHash();
    api
      .post("/auth/verify-email", { token })
      .then(() => setState("ok"))
      .catch((e) => setState(e as ApiError));
  }, []);
  return (
    <AuthLayout>
      <Heading title={t("Confirmação de e-mail")} />
      {state === "loading" && <Loading label={t("Confirmando…")} />}
      {state === "ok" && (
        <div className="alert alert-ok" role="status">
          <p>{t("E-mail confirmado.")}</p>
        </div>
      )}
      {state instanceof ApiError && <FormError error={state} />}
      <div className="auth-links">
        <Link to="/">{t("Continuar")}</Link>
      </div>
    </AuthLayout>
  );
}

/** Senha provisória (gerada pelo sistema): a conta só segue depois de escolher uma senha própria. */
export function ForcedPasswordChangePage() {
  const { me, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <Heading title={t("Senha alterada")} />
        <div className="alert alert-ok" role="status">
          <p>{t("Sua senha foi trocada e as outras sessões foram encerradas. Entre de novo com a nova senha.")}</p>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button variant="primary" block onClick={() => void logout()}>
            {t("Ir para o login")}
          </Button>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <Heading
        title={t("Troque sua senha provisória")}
        intro={t("{full_name} ({email}) — por segurança, escolha uma senha só sua antes de continuar.", { full_name: me?.full_name ?? "", email: me?.email ?? "" })}
      />
      <form className="form" onSubmit={submit} noValidate>
        {error && <FormError error={error} />}
        <TextField
          label={t("Senha provisória")}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
        <TextField
          label={t("Nova senha")}
          type="password"
          autoComplete="new-password"
          hint={t("Mínimo de 10 caracteres, com letras e números; diferente da provisória.")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <TextField
          label={t("Confirme a nova senha")}
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? t("As senhas não conferem.") : undefined}
          required
        />
        <Button type="submit" variant="primary" busy={busy} block>
          {t("Salvar nova senha")}
        </Button>
      </form>
      <div className="auth-links">
        <button type="button" className="link-button" onClick={() => void logout()}>
          {t("Sair")}
        </button>
      </div>
    </AuthLayout>
  );
}
